import { usePathname, useRouter } from "expo-router";
import {
  Text,
  Pressable,
  ScrollView,
  LayoutChangeEvent,
  View,
} from "react-native";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import {
  CreditCard,
  File,
  Image,
  LucideIcon,
  TrendingUp,
  UserCircle,
} from "lucide-react-native";
import { useSettingsData } from "../../../contexts/SettingsDataContext";

type Tab = {
  name: string;
  label: string;
  path: string;
  icon: LucideIcon;
};

let tabs: Tab[] = [
  {
    name: "payouts",
    label: "Payouts",
    path: "/earnings/payouts",
    icon: TrendingUp,
  },
  {
    name: "transactions",
    label: "Transactions",
    path: "/earnings/transactions",
    icon: CreditCard,
  },
  {
    name: "account",
    label: "Account",
    path: "/earnings/account",
    icon: UserCircle,
  },
  {
    name: "branding",
    label: "Branding",
    path: "/earnings/branding",
    icon: Image,
  },
  {
    name: "documents",
    label: "Documents",
    path: "/earnings/documents",
    icon: File,
  },
];

export default function EarningsTabs() {
  const router = useRouter();
  const pathname = usePathname();
  const posthog = usePostHog();
  const scrollViewRef = useRef<ScrollView>(null);
  const [scrollViewWidth, setScrollViewWidth] = useState(0);

  useEffect(() => {
    const currentTabIndex = tabs.findIndex((tab) => tab.path === pathname);
    if (currentTabIndex !== -1) {
      scrollTabToCenter(currentTabIndex);
    }
  }, [pathname, scrollViewWidth]);

  function scrollTabToCenter(tabIndex: number) {
    if (!scrollViewWidth) return;
    const totalWidth = tabs.length * (scrollViewWidth / tabs.length);
    let scrollPosition =
      (totalWidth - scrollViewWidth) / 2 +
      (scrollViewWidth / tabs.length) * tabIndex;
    if (tabIndex <= 2) {
      scrollPosition = 0;
    }
    scrollViewRef.current?.scrollTo({ x: scrollPosition, animated: true });
  }

  const handleTabPress = (tab: Tab) => {
    posthog.capture("billing_tab_clicked", {
      tab_name: tab.name,
    });
    router.push(tab.path);
  };

  const handleScrollViewLayout = (event: LayoutChangeEvent) => {
    setScrollViewWidth(event.nativeEvent.layout.width);
  };

  const { stripeAccountStatus } = useSettingsData();
  tabs = tabs.filter((tab) => {
    if (tab.name === "account") {
      return true;
    }
    return stripeAccountStatus !== "NONE";
  });

  return (
    <View className="border-b border-gray-200 bg-white">
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onLayout={handleScrollViewLayout}
        className="flex-row pb-1"
      >
        {tabs.map((tab, index) => {
          const isActive = pathname === tab.path;
          const TabIcon = tab.icon;

          return (
            <Pressable
              key={tab.name}
              onPress={() => handleTabPress(tab)}
              className={`flex-row items-center px-4 py-3 pt-3 border-b-2 ${
                isActive
                  ? "border-blue-500"
                  : "border-transparent hover:bg-gray-50"
              }`}
            >
              <TabIcon
                size={16}
                color={isActive ? "#3b82f6" : "#6b7280"}
                className="mr-2"
              />
              <Text
                className={`font-medium text-sm ${
                  isActive ? "text-blue-600" : "text-gray-600"
                }`}
              >
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}
